import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { VIEW_SOURCE_MODE_VALUE } from '@/shared/config';
import { TEMPLATE_GAIN_RATIO } from '@/widgets/beats/ui/reviewBeatsHelper';
import TemplatePreview from '@/widgets/beats/ui/templatePreview';
import './styles.scss';
import TemplateAction from './templateAction';
import useTemplateOverview from './useTemplateOverview';

export type TemplateOverviewProps = {};

/**
 * Renders the right-hand morphology template column of the beats tab. Shows ten skeleton placeholders while useTemplateOverview reports templates are loading, then either a no-morphology message or one TemplatePreview per merged template, hiding the reviewed status when the beat source filter is not ALL.
 *
 * @param props - Template overview inputs; the displayed data itself comes from useTemplateOverview.
 * @returns The template overview column with its action bar and template list.
 */
const TemplateOverview = (props: TemplateOverviewProps) => {
  const {
    columnContentRef,
    isLoadingTemplate,
    mergeTemplateList,
    selectedTemplate,
    onClickSelect,
    ecgDataMap,
    viewBeatsSource,
    reviewedBeatsChanges,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
  } = useTemplateOverview();

  /**
   * Builds the morphology template list rendered in the beats tab right column. Emits a no-morphology message when mergeTemplateList is empty and otherwise a small-size TemplatePreview per template, marking the selected one active and hiding the reviewed status when the beat source filter is not ALL.
   *
   * @returns The container holding one preview card per morphology template.
   */
  const renderTemplate = () => (
    <div className="right-column-template-container">
      {_.isEmpty(mergeTemplateList) && (
        <div className="no-morphology-display">There is no morphology</div>
      )}
      {_.map(mergeTemplateList, (x) => (
        <TemplatePreview
          isActive={selectedTemplate?.id === x?.id}
          template={x}
          onClickSelect={() => onClickSelect(x)}
          isUnclassified={x.unclassified}
          beatCount={x.total}
          ecgDataMap={ecgDataMap}
          gain={7.5 * TEMPLATE_GAIN_RATIO.SMALL_SIZE} // default gain is 7.5
          isSmallSize
          name={x.name}
          isIncluded={x.isIncludedToReport}
          hideStatus={viewBeatsSource.value !== VIEW_SOURCE_MODE_VALUE.ALL}
          selectedTemplate={x}
          reviewedBeatsChanges={reviewedBeatsChanges}
          bulkReviewedBeats={bulkReviewedBeats}
          resetBulkReviewedBeats={resetBulkReviewedBeats}
          id={`template-overview-${x.id}`}
        />
      ))}
    </div>
  );

  return (
    <div className="template-overview-container">
      <TemplateAction />
      <div ref={columnContentRef} className="column-content">
        {isLoadingTemplate ? (
          <div className="right-column-template-container">
            {_.map(_.range(10), (i) => (
              <div key={`skeleton-template-${i}`}>
                <Skeleton height={120} width={100} />
              </div>
            ))}
          </div>
        ) : (
          renderTemplate()
        )}
      </div>
    </div>
  );
};

export default TemplateOverview;
